/* ═══════════════════════════════════════════════════════════════════
   BriefAuthor — Step 3 Creative Brief authoring surface.

   Cross-plane schema (vendored from services.brief_schema_v1,
   SSP-published fingerprint
   f424908ba72f64f67e1ef674600f52ffc287fcb13e17edb35a1274911af9e27d):

     goal              : enum ∈ {awareness, consideration, conversion, brand_fit}
     format_spec       : STRUCTURED — {aspect_ratio enum, duration_seconds_max int,
                                       hook_first_seconds int, voiceover_required bool,
                                       b_roll_required bool}
     message_points    : list[str], cap 3, item 1..240
     creative_freedom  : str, 1..500

   Authoring contract (Build-Group A):
     • The brief is REQUIRED at /generate-rfps — there is no fire-without-
       brief escape hatch by construction (backend Pydantic enforces it).
     • Across a ladder of waves the brief structure is FIXED. The
       "Clone from previous wave" picker guarantees byte-identity across
       a ladder; the cloned-from chip is provenance.
     • Premium-campaign framing only. NO "test / experiment / cohort /
       dose / ladder / pilot / test wave" language anywhere creator-facing.
     • Structured format_spec is the experimental control — every field
       is machine-checkable, identical-by-construction across the ladder.

   Wired by: components/wave-builder/Step3Review.jsx (above the Generate
   RFPs CTA) so the brand reviews the brief alongside the wave summary
   before firing.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, Plus, Trash2, Copy, Check, History, AlertCircle } from 'lucide-react';
import { T, P, CARD, BORDER } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

// Mirrors services.brief_schema_v1 SSP-canonical constants.
const SCHEMA = {
  goals: ['awareness', 'consideration', 'conversion', 'brand_fit'],
  aspect_ratios: ['9:16', '1:1', '16:9', 'free'],
  message_points_max: 3,
  message_point_item_max: 240,
  creative_freedom_max: 500,
  duration_seconds_upper: 180,
  hook_first_seconds_upper: 10,
};

const FORBIDDEN_FRAMING = ['experiment', 'study', 'cohort', 'dose', 'ladder', 'test wave', 'pilot'];

export const EMPTY_BRIEF = {
  goal: '',
  format_spec: {
    aspect_ratio: '9:16',
    duration_seconds_max: 60,
    hook_first_seconds: 3,
    voiceover_required: false,
    b_roll_required: false,
  },
  message_points: [''],
  creative_freedom: '',
};

function findForbiddenTokens(text) {
  if (!text) return [];
  const lc = String(text).toLowerCase();
  return FORBIDDEN_FRAMING.filter(token => lc.includes(token));
}

export function validateBrief(brief) {
  const errors = {};
  // goal
  if (!brief.goal) errors.goal = 'Pick a campaign goal.';
  else if (!SCHEMA.goals.includes(brief.goal)) errors.goal = 'Invalid goal.';
  // format_spec
  const fs = brief.format_spec || {};
  const fsErr = {};
  if (!SCHEMA.aspect_ratios.includes(fs.aspect_ratio)) fsErr.aspect_ratio = 'Invalid aspect ratio.';
  const dur = Number(fs.duration_seconds_max);
  if (!Number.isInteger(dur) || dur < 1 || dur > SCHEMA.duration_seconds_upper) {
    fsErr.duration_seconds_max = `1 to ${SCHEMA.duration_seconds_upper} seconds.`;
  }
  const hook = Number(fs.hook_first_seconds);
  if (!Number.isInteger(hook) || hook < 0 || hook > SCHEMA.hook_first_seconds_upper) {
    fsErr.hook_first_seconds = `0 to ${SCHEMA.hook_first_seconds_upper} seconds.`;
  }
  if (typeof fs.voiceover_required !== 'boolean') fsErr.voiceover_required = 'Required.';
  if (typeof fs.b_roll_required !== 'boolean') fsErr.b_roll_required = 'Required.';
  if (Object.keys(fsErr).length) errors.format_spec = fsErr;
  // message_points
  const pts = (brief.message_points || []).map(p => (p || '').trim()).filter(Boolean);
  if (pts.length < 1) errors.message_points = 'Need at least 1 message point.';
  else if (pts.length > SCHEMA.message_points_max) errors.message_points = `Max ${SCHEMA.message_points_max} message points.`;
  else if (pts.some(p => p.length > SCHEMA.message_point_item_max)) {
    errors.message_points = `Each point max ${SCHEMA.message_point_item_max} chars.`;
  }
  // creative_freedom
  const cf = (brief.creative_freedom || '').trim();
  if (!cf) errors.creative_freedom = 'Required.';
  else if (cf.length > SCHEMA.creative_freedom_max) errors.creative_freedom = `Max ${SCHEMA.creative_freedom_max} chars.`;
  // framing — check the text fields only (enums + numerics can't carry slang)
  const framingText = [...pts, cf].join(' ');
  const bad = findForbiddenTokens(framingText);
  if (bad.length) errors.framing = `Avoid: ${bad.join(', ')}. Use premium-campaign framing.`;
  return errors;
}

export function isBriefValid(brief) {
  return Object.keys(validateBrief(brief)).length === 0;
}

export default function BriefAuthor({ brief, onChange, clonedFrom, onCloneCleared }) {
  const safe = brief || EMPTY_BRIEF;
  const errors = useMemo(() => validateBrief(safe), [safe]);
  const ok = Object.keys(errors).length === 0;
  const fsErr = errors.format_spec || {};

  // Recent-briefs picker state.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [recent, setRecent] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState(null);

  useEffect(() => {
    if (!pickerOpen || recent !== null) return;
    let cancelled = false;
    (async () => {
      setRecentLoading(true);
      setRecentError(null);
      try {
        const { data } = await axios.get(`${API}/api/brand/wave-briefs/recent`, { withCredentials: true });
        if (!cancelled) setRecent(data.items || []);
      } catch {
        if (!cancelled) setRecentError('Could not load previous briefs.');
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickerOpen, recent]);

  const setField = (key, value) => onChange({ ...safe, [key]: value });
  const setFsField = (key, value) => onChange({ ...safe, format_spec: { ...safe.format_spec, [key]: value } });

  const setPoint = (idx, value) => {
    const next = [...(safe.message_points || [])];
    next[idx] = value;
    onChange({ ...safe, message_points: next });
  };

  const addPoint = () => {
    if ((safe.message_points || []).length >= SCHEMA.message_points_max) return;
    onChange({ ...safe, message_points: [...(safe.message_points || []), ''] });
  };

  const removePoint = (idx) => {
    const cur = safe.message_points || [];
    if (cur.length <= 1) return;
    onChange({ ...safe, message_points: cur.filter((_, i) => i !== idx) });
  };

  const cloneFrom = (item) => {
    const b = item.brief || {};
    const incomingFs = b.format_spec && typeof b.format_spec === 'object' ? b.format_spec : {};
    onChange({
      goal: b.goal || '',
      format_spec: {
        aspect_ratio: incomingFs.aspect_ratio || '9:16',
        duration_seconds_max: incomingFs.duration_seconds_max || 60,
        hook_first_seconds: incomingFs.hook_first_seconds ?? 3,
        voiceover_required: !!incomingFs.voiceover_required,
        b_roll_required: !!incomingFs.b_roll_required,
      },
      message_points: (b.message_points && b.message_points.length > 0)
        ? [...b.message_points]
        : [''],
      creative_freedom: b.creative_freedom || '',
    });
    onCloneCleared?.({
      wave_id: item.wave_id,
      wave_name: item.wave_name,
      cloned_at: new Date().toISOString(),
    });
    setPickerOpen(false);
  };

  return (
    <div
      className="rounded-xl p-5 space-y-4"
      style={{ background: CARD, border: `1px solid ${ok ? BORDER : 'rgba(255,77,148,0.4)'}` }}
      data-testid="brief-author"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
            style={{ background: 'rgba(0,245,255,0.12)' }}
          >
            <FileText className="h-4 w-4" style={{ color: T }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-black text-white">Creative Brief</h3>
            <p className="text-[11px] text-gray-500 leading-snug mt-0.5">
              Structured controls every creator sees on the RFP card — fixed across every wave, your content.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {clonedFrom && (
            <div
              className="text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5"
              data-testid="brief-cloned-from-chip"
              style={{ background: 'rgba(0,245,255,0.1)', color: T, border: `1px solid ${T}55` }}
              title={`Cloned from ${clonedFrom.wave_name}`}
            >
              <Copy className="h-3 w-3" />
              <span className="truncate max-w-[16ch]">Cloned from {clonedFrom.wave_name}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => setPickerOpen(o => !o)}
            data-testid="brief-clone-picker-toggle"
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5 transition-colors"
            style={{
              background: pickerOpen ? `${T}22` : 'transparent',
              color: pickerOpen ? T : '#a1a1aa',
              border: `1px solid ${pickerOpen ? T : BORDER}`,
            }}
          >
            <History className="h-3 w-3" />
            {pickerOpen ? 'Hide history' : 'Clone from previous wave'}
          </button>
        </div>
      </div>

      {/* Clone-from-previous picker — same-ladder integrity guarantee. */}
      {pickerOpen && (
        <div
          className="rounded-lg p-3 space-y-2"
          data-testid="brief-clone-picker"
          style={{ background: 'rgba(0,245,255,0.04)', border: `1px dashed ${T}55` }}
        >
          {recentLoading && <p className="text-[11px] text-gray-400">Loading previous briefs…</p>}
          {recentError && <p className="text-[11px] text-[#FF6B6B]">{recentError}</p>}
          {!recentLoading && !recentError && recent && recent.length === 0 && (
            <p className="text-[11px] text-gray-500" data-testid="brief-clone-empty">
              No previous waves with a brief yet. The next one becomes the canonical brief for this ladder.
            </p>
          )}
          {!recentLoading && !recentError && recent && recent.length > 0 && (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {recent.map(item => (
                <li key={item.wave_id}>
                  <button
                    type="button"
                    onClick={() => cloneFrom(item)}
                    data-testid={`brief-clone-row-${item.wave_id}`}
                    className="w-full text-left rounded-lg px-3 py-2 transition-colors flex items-start justify-between gap-3 hover:bg-white/5"
                    style={{ border: `1px solid ${BORDER}` }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold text-white truncate">{item.wave_name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{item.preview}</p>
                    </div>
                    <span
                      className="text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ background: 'rgba(0,245,255,0.1)', color: T }}
                    >
                      {item.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* goal — enum */}
      <Field label="Goal" hint="The campaign objective the creator sees on their card." error={errors.goal}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {SCHEMA.goals.map(g => (
            <button
              key={g}
              type="button"
              onClick={() => setField('goal', g)}
              data-testid={`brief-goal-${g}`}
              className="px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors"
              style={{
                background: safe.goal === g ? `${T}22` : 'transparent',
                color: safe.goal === g ? T : '#a1a1aa',
                border: `1px solid ${safe.goal === g ? T : BORDER}`,
              }}
            >
              {g.replace('_', ' ')}
            </button>
          ))}
        </div>
      </Field>

      {/* format_spec — structured controls (NOT a free string) */}
      <Field label="Format spec" hint="Machine-checkable constraints — identical across every wave in the ladder.">
        <div
          className="rounded-lg p-3 space-y-3"
          data-testid="brief-format-spec"
          style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}` }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* aspect_ratio */}
            <div>
              <label className="text-[9px] uppercase tracking-widest font-black text-gray-500 block mb-1.5">
                Aspect ratio
              </label>
              <div className="flex gap-1.5">
                {SCHEMA.aspect_ratios.map(ar => (
                  <button
                    key={ar}
                    type="button"
                    onClick={() => setFsField('aspect_ratio', ar)}
                    data-testid={`brief-aspect-ratio-${ar}`}
                    className="flex-1 px-2 py-1.5 rounded text-[10px] font-bold transition-colors"
                    style={{
                      background: safe.format_spec.aspect_ratio === ar ? `${T}22` : 'transparent',
                      color: safe.format_spec.aspect_ratio === ar ? T : '#a1a1aa',
                      border: `1px solid ${safe.format_spec.aspect_ratio === ar ? T : BORDER}`,
                    }}
                  >
                    {ar}
                  </button>
                ))}
              </div>
              {fsErr.aspect_ratio && <p className="mt-1 text-[10px] text-[#FF4D94]">{fsErr.aspect_ratio}</p>}
            </div>

            {/* duration_seconds_max */}
            <NumericField
              label="Duration (max)"
              suffix="s"
              testId="brief-duration-seconds-max"
              value={safe.format_spec.duration_seconds_max}
              min={1}
              max={SCHEMA.duration_seconds_upper}
              onChange={v => setFsField('duration_seconds_max', v)}
              error={fsErr.duration_seconds_max}
            />

            {/* hook_first_seconds */}
            <NumericField
              label="Hook lands in"
              suffix="s"
              testId="brief-hook-first-seconds"
              value={safe.format_spec.hook_first_seconds}
              min={0}
              max={SCHEMA.hook_first_seconds_upper}
              onChange={v => setFsField('hook_first_seconds', v)}
              error={fsErr.hook_first_seconds}
            />

            {/* toggles col */}
            <div className="grid grid-cols-2 gap-2 col-span-1 sm:col-span-1">
              <ToggleField
                label="Voiceover"
                testId="brief-voiceover-required"
                value={safe.format_spec.voiceover_required}
                onChange={v => setFsField('voiceover_required', v)}
              />
              <ToggleField
                label="B-roll"
                testId="brief-b-roll-required"
                value={safe.format_spec.b_roll_required}
                onChange={v => setFsField('b_roll_required', v)}
              />
            </div>
          </div>
        </div>
      </Field>

      {/* message_points */}
      <Field
        label={`Message points (${(safe.message_points || []).filter(p => p && p.trim()).length} of 1–${SCHEMA.message_points_max})`}
        hint="Up to 3 things the creator must cover. They flex everything else."
        error={errors.message_points}
      >
        <div className="space-y-2">
          {(safe.message_points || []).map((pt, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span
                className="text-[10px] font-black mt-2.5 w-5 text-center flex-shrink-0"
                style={{ color: T }}
              >
                {idx + 1}.
              </span>
              <div className="flex-1">
                <input
                  type="text"
                  value={pt || ''}
                  onChange={e => setPoint(idx, e.target.value)}
                  maxLength={SCHEMA.message_point_item_max}
                  placeholder={idx === 0 ? 'e.g. Mention the launch is this week' : 'e.g. Show the product in a natural morning routine'}
                  data-testid={`brief-message-point-${idx}`}
                  className="w-full bg-transparent text-xs text-white rounded-lg px-3 py-2.5 outline-none"
                  style={{ border: `1px solid ${BORDER}` }}
                />
                <CharCount value={pt} max={SCHEMA.message_point_item_max} />
              </div>
              {(safe.message_points || []).length > 1 && (
                <button
                  type="button"
                  onClick={() => removePoint(idx)}
                  data-testid={`brief-remove-point-${idx}`}
                  className="mt-1.5 p-1.5 rounded-lg text-gray-500 hover:text-[#FF4D94] hover:bg-[#FF4D94]/10 transition-colors"
                  aria-label={`Remove point ${idx + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {(safe.message_points || []).length < SCHEMA.message_points_max && (
            <button
              type="button"
              onClick={addPoint}
              data-testid="brief-add-point"
              className="text-[10px] font-bold flex items-center gap-1.5 mt-1 px-2 py-1 rounded transition-colors hover:bg-white/5"
              style={{ color: T }}
            >
              <Plus className="h-3 w-3" /> Add another point (up to {SCHEMA.message_points_max})
            </button>
          )}
        </div>
      </Field>

      {/* creative_freedom */}
      <Field
        label="Creative freedom"
        hint="What the creator is free to flex — voice, angle, anecdote, format choice."
        error={errors.creative_freedom}
      >
        <textarea
          value={safe.creative_freedom || ''}
          onChange={e => setField('creative_freedom', e.target.value)}
          maxLength={SCHEMA.creative_freedom_max}
          rows={3}
          placeholder="e.g. Use your own voice. Frame the product around your existing morning-routine content. No script — keep it authentic."
          data-testid="brief-creative-freedom"
          className="w-full bg-transparent text-xs text-white rounded-lg px-3 py-2.5 outline-none resize-none leading-relaxed"
          style={{ border: `1px solid ${errors.creative_freedom ? '#FF4D94' : BORDER}` }}
        />
        <CharCount value={safe.creative_freedom} max={SCHEMA.creative_freedom_max} />
      </Field>

      {/* framing violation banner */}
      {errors.framing && (
        <div
          className="rounded-lg px-3 py-2.5 flex items-start gap-2"
          data-testid="brief-framing-error"
          style={{ background: 'rgba(255,77,148,0.08)', border: '1px solid rgba(255,77,148,0.3)' }}
        >
          <AlertCircle className="h-3.5 w-3.5 text-[#FF4D94] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#FF4D94]">Framing</p>
            <p className="text-[11px] text-gray-300 mt-0.5">{errors.framing}</p>
          </div>
        </div>
      )}

      {ok && (
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-green-400" data-testid="brief-ok">
          <Check className="h-3 w-3" /> Brief locks in cleanly. Identical structure across every wave.
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-black text-gray-500 block mb-1">
        {label}
      </label>
      {hint && <p className="text-[10px] text-gray-600 mb-2">{hint}</p>}
      {children}
      {error && typeof error === 'string' && (
        <p className="mt-1 text-[10px] text-[#FF4D94]" data-testid="brief-field-error">{error}</p>
      )}
    </div>
  );
}

function NumericField({ label, suffix, testId, value, min, max, onChange, error }) {
  return (
    <div>
      <label className="text-[9px] uppercase tracking-widest font-black text-gray-500 block mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-1.5 rounded-lg" style={{ border: `1px solid ${error ? '#FF4D94' : BORDER}` }}>
        <input
          type="number"
          value={value ?? ''}
          onChange={e => {
            const raw = e.target.value;
            onChange(raw === '' ? '' : Math.max(min, Math.min(max, Number(raw))));
          }}
          min={min}
          max={max}
          data-testid={testId}
          className="flex-1 bg-transparent text-xs text-white px-3 py-2 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        {suffix && <span className="pr-3 text-[10px] text-gray-500 font-bold">{suffix}</span>}
      </div>
      {error && <p className="mt-1 text-[10px] text-[#FF4D94]" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}

function ToggleField({ label, testId, value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={testId}
      className="rounded-lg px-3 py-2 flex items-center justify-between transition-colors"
      style={{
        background: value ? `${T}22` : 'transparent',
        color: value ? T : '#a1a1aa',
        border: `1px solid ${value ? T : BORDER}`,
      }}
      aria-pressed={value}
    >
      <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
      <span className="text-[10px] font-black">{value ? 'On' : 'Off'}</span>
    </button>
  );
}

function CharCount({ value, max }) {
  const len = (value || '').length;
  const over = len > max;
  const near = len > max * 0.85;
  return (
    <p
      className="mt-1 text-[9px] text-right tabular-nums"
      style={{ color: over ? '#FF4D94' : near ? P : '#52525b' }}
    >
      {len} / {max}
    </p>
  );
}
